// The out-of-band approval secret S (spec section 6.1, decision 15; #1693).
//
// WHAT IT IS FOR. Under `v: 1` the approving device wraps MK to whatever public
// key the API hands it, so a party controlling the running API during an
// approval could present its own key, receive MK, and re-wrap it to the real
// device. S closes that: the pending device generates it, shows it on its own
// screen, and it never touches the server. The wrapping key becomes
// `HKDF(ECDH shared secret || S)`, so a substituted public key yields a
// wrapping nobody can open, and a substituted wrapping fails on the new device.
// The price is that both phones have to be in the same place at the same time,
// which is the same constraint WhatsApp's linked devices impose.
//
// TWO CARRIERS, ONE SECRET. The QR code holds `{ deviceId, publicKey, s }` with
// the full 128 bits. An approver with no camera types sixteen Crockford base32
// characters instead: FIFTEEN carry the first seventy-five bits of the same S
// — a prefix, not a second secret — and the SIXTEENTH is a check character.
//
// ⚠️ WHY A CHECK CHARACTER, AND WHY IT COST FIVE BITS. The approving phone
// cannot verify a typed code against anything: it carries no public key, and
// S is never on the server. So before this, ANY sixteen valid characters were
// taken and approved, and a mistyped code failed only on the waiting phone,
// as a wrapping that would not open — a member who misread a 5 as an S was
// told "accepted" on one phone and left holding nothing on the other. The
// check character makes the code refuse itself: it is Crockford's own scheme,
// the fifteen data characters read as a base-32 number modulo 37. Thirty-seven
// is a prime above thirty-two, which is the whole point — 32^k is invertible
// mod 37, so EVERY single wrong character moves the check, and (1 − 32) is too,
// so every adjacent transposition does as well. A mod-32 sum would be blind to
// transpositions and a mod-31 one blind to them too. Seventy-five bits remain,
// far past guessing inside a live approval that also needs a two-minute server
// nonce and a signature the approver's bundle key makes.
//
// Crockford pays for the modulus with five extra check symbols (`*~$=U`) for
// values 32–36. This app does not: they are awkward on a phone keyboard and
// the screens promise "never contains the letter U". Instead the WAITING
// device — the one that mints S — draws again until the check lands inside the
// alphabet (`generateApprovalSecret`; 32/37 of draws do). A secret from before
// this rule may not be typeable, and `isTypeableApprovalSecret` is how the
// identity code decides to replace it.
//
// WHICH MEANS THE APPROVER'S SECRET MAY BE 16 BYTES OR THE TYPED 75 BITS, and
// the pending device cannot know which carrier was used — nothing tells it. So
// it does not guess: `approvalSecretCandidates` returns both, longest first,
// and the unwrap tries each. That is safe rather than sloppy because AES-GCM
// authenticates: a candidate that is not the one the approver used fails its
// tag check and is indistinguishable from any other wrong key. Trying two
// costs one extra derive on a path a member takes once per device.
import { CROCKFORD_ALPHABET, base64ToBytes, bytesToBase64 } from './encoding';
import { randomBytes } from './webcrypto';

/** The secret in full, as the QR carries it. */
export const APPROVAL_SECRET_BYTES = 16;

/** Sixteen characters: fifteen of the secret, then the check character. */
export const TYPED_CODE_LENGTH = 16;
export const TYPED_CODE_GROUP_LENGTH = 4;
/** The data characters, five bits each: seventy-five bits of S. */
export const TYPED_DATA_LENGTH = 15;
export const TYPED_SECRET_BITS = TYPED_DATA_LENGTH * 5;
/** Those bits packed MSB-first: nine whole bytes and the top three of a tenth. */
const TYPED_SECRET_BYTES = 10;
const TYPED_LAST_BYTE_MASK = 0xe0;
/** Crockford's check modulus. Prime and above 32: see the header for why both matter. */
const CHECK_MODULUS = 37;
/**
 * Draws before `generateApprovalSecret` gives up. Each fails with probability
 * 5/37, so this is (5/37)^64 ≈ 10^-56 — a bound on a loop, not a case.
 */
const MAX_SECRET_DRAWS = 64;

export const APPROVAL_SECRET_ERRORS = Object.freeze({
  /** What was typed is not sixteen Crockford characters. */
  MALFORMED: 'MALFORMED',
  /** Sixteen valid characters whose check character disagrees with the other fifteen. */
  CHECK_FAILED: 'CHECK_FAILED',
  /** A secret whose check value has no character in the alphabet (from before the rule). */
  UNTYPEABLE: 'UNTYPEABLE',
  /** A QR payload that is not `{ deviceId, publicKey, s }`. */
  MALFORMED_PAYLOAD: 'MALFORMED_PAYLOAD',
});

export class ApprovalSecretError extends Error {
  /**
   * @param {keyof typeof APPROVAL_SECRET_ERRORS} code
   * @param {string} message
   */
  constructor(code, message) {
    super(message);
    this.name = 'ApprovalSecretError';
    this.code = code;
  }
}

/**
 * A fresh S, generated by the PENDING device beside its key pair — drawn again
 * until its typed code's check character exists in the alphabet (see header).
 * Rejection sampling over a uniform source is still uniform over what is kept,
 * so nothing about S's entropy changes except that five in thirty-seven values
 * are never chosen.
 *
 * @returns {Uint8Array} 16 bytes
 */
export function generateApprovalSecret() {
  for (let draw = 0; draw < MAX_SECRET_DRAWS; draw += 1) {
    const secret = randomBytes(APPROVAL_SECRET_BYTES);
    if (isTypeableApprovalSecret(secret)) return secret;
  }
  // Not a case: see MAX_SECRET_DRAWS. Thrown rather than returning an
  // untypeable secret, which would strand the member on a code they cannot
  // be shown.
  throw new Error('generateApprovalSecret: the random source is not behaving');
}

/**
 * The fifteen data characters: the first seventy-five bits of S, five at a
 * time across byte boundaries.
 *
 * (The recovery code takes one random byte per character and masks to five
 * bits, which is fine when the bytes are generated for the purpose; here the
 * bytes are S, and throwing away three bits of each would leave the typed
 * carrier holding far less than it claims to.)
 *
 * @param {Uint8Array} secret the full 16-byte S
 * @returns {string} fifteen characters
 */
function dataCharsOf(secret) {
  const bits = secret.subarray(0, TYPED_SECRET_BYTES);
  let out = '';
  let acc = 0;
  let held = 0;
  for (const byte of bits) {
    acc = (acc << 8) | byte;
    held += 8;
    while (held >= 5 && out.length < TYPED_DATA_LENGTH) {
      held -= 5;
      out += CROCKFORD_ALPHABET[(acc >> held) & 31];
    }
  }
  return out;
}

/**
 * Crockford's check over the data characters: their value as a base-32 number,
 * modulo 37. Reduced as it goes, so seventy-five bits never need a BigInt.
 *
 * @param {string} data the fifteen data characters, already normalised
 * @returns {number} 0–36
 */
export function checkValueFor(data) {
  let acc = 0;
  for (const char of data) {
    acc = (acc * CROCKFORD_ALPHABET.length + CROCKFORD_ALPHABET.indexOf(char)) % CHECK_MODULUS;
  }
  return acc;
}

/**
 * Whether this S has a typed code at all — that is, whether its check value
 * is one of the thirty-two the alphabet can show. False for anything that is
 * not a 16-byte secret, so the identity code can ask without a try/catch.
 *
 * @param {unknown} secret
 * @returns {boolean}
 */
export function isTypeableApprovalSecret(secret) {
  if (!(secret instanceof Uint8Array) || secret.length !== APPROVAL_SECRET_BYTES) return false;
  return checkValueFor(dataCharsOf(secret)) < CROCKFORD_ALPHABET.length;
}

/**
 * The typed carrier: fifteen characters of S and one check character.
 *
 * @param {Uint8Array} secret the full 16-byte S
 * @returns {string} sixteen characters, ungrouped
 * @throws {ApprovalSecretError} UNTYPEABLE for a secret whose check value the
 *   alphabet cannot show — only possible for one minted before the rule, which
 *   `ensureLocalIdentity` replaces rather than displays
 */
export function typedCodeFor(secret) {
  assertSecret(secret);
  const data = dataCharsOf(secret);
  const check = checkValueFor(data);
  if (check >= CROCKFORD_ALPHABET.length) {
    throw new ApprovalSecretError(
      APPROVAL_SECRET_ERRORS.UNTYPEABLE,
      'This secret has no typed code; mint another',
    );
  }
  return data + CROCKFORD_ALPHABET[check];
}

/** `ABCD-EFGH-JKMN-PQRS`: four groups of four, the form shown to a member. */
export function formatTypedCode(code) {
  const normalised = normaliseTypedCode(code);
  const groups = [];
  for (let i = 0; i < TYPED_CODE_LENGTH; i += TYPED_CODE_GROUP_LENGTH) {
    groups.push(normalised.slice(i, i + TYPED_CODE_GROUP_LENGTH));
  }
  return groups.join('-');
}

/**
 * Case, spaces and separators are forgiven, and O, I and L are read as 0 and 1,
 * the way Crockford intends — a member is copying this off another screen.
 *
 * @param {string} input
 * @returns {string} the sixteen canonical characters
 */
export function normaliseTypedCode(input) {
  if (typeof input !== 'string') {
    throw new ApprovalSecretError(APPROVAL_SECRET_ERRORS.MALFORMED, 'Enter the code');
  }
  const folded = input
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1');
  if (folded.length !== TYPED_CODE_LENGTH) {
    throw new ApprovalSecretError(
      APPROVAL_SECRET_ERRORS.MALFORMED,
      `The code has ${TYPED_CODE_LENGTH} characters; this one has ${folded.length}`,
    );
  }
  if (Array.from(folded).some(char => !CROCKFORD_ALPHABET.includes(char))) {
    throw new ApprovalSecretError(
      APPROVAL_SECRET_ERRORS.MALFORMED,
      'The code never contains the letter U; check it and try again',
    );
  }
  return folded;
}

/**
 * The seventy-five bits a typed code stands for, as ten bytes — what the
 * APPROVER derives from. Byte for byte what `typedSecretOf` gives the waiting
 * device, which is the whole contract between the two.
 *
 * @param {string} input what the member typed
 * @returns {Uint8Array} 10 bytes; the last carries only its top three bits
 * @throws {ApprovalSecretError} MALFORMED when it is not sixteen Crockford
 *   characters; CHECK_FAILED when it is, and one of them is wrong — the
 *   member's typo, caught here rather than on the other phone
 */
export function secretFromTypedCode(input) {
  const normalised = normaliseTypedCode(input);
  const data = normalised.slice(0, TYPED_DATA_LENGTH);
  const typedCheck = CROCKFORD_ALPHABET.indexOf(normalised[TYPED_DATA_LENGTH]);
  if (typedCheck !== checkValueFor(data)) {
    // Which character is not said, because the check cannot know: it knows the
    // sixteen do not agree, not which one to blame.
    throw new ApprovalSecretError(
      APPROVAL_SECRET_ERRORS.CHECK_FAILED,
      'One character of the code is wrong',
    );
  }

  const out = new Uint8Array(TYPED_SECRET_BYTES);
  let acc = 0;
  let held = 0;
  let at = 0;
  for (const char of data) {
    acc = (acc << 5) | CROCKFORD_ALPHABET.indexOf(char);
    held += 5;
    if (held >= 8) {
      held -= 8;
      out[at] = (acc >> held) & 0xff;
      at += 1;
    }
  }
  // 75 = 9 × 8 + 3: the last three bits are the top of the tenth byte.
  if (held > 0) out[at] = (acc << (8 - held)) & 0xff;
  return out;
}

/**
 * The typed carrier's bytes, as the WAITING device holds them: the first ten
 * bytes of S with the tenth masked to its top three bits. What the approver
 * derives from a correctly typed code, exactly.
 *
 * @param {Uint8Array} secret the full 16-byte S
 * @returns {Uint8Array} 10 bytes
 */
export function typedSecretOf(secret) {
  assertSecret(secret);
  const out = secret.slice(0, TYPED_SECRET_BYTES);
  out[TYPED_SECRET_BYTES - 1] &= TYPED_LAST_BYTE_MASK;
  return out;
}

/**
 * Both forms of this device's own S, longest first, for the unwrap to try.
 *
 * The pending device is never told which carrier the approver used, so it
 * offers both rather than guessing. See the header for why trying two is safe.
 *
 * @param {Uint8Array} secret the full 16-byte S this device generated
 * @returns {Uint8Array[]}
 */
export function approvalSecretCandidates(secret) {
  assertSecret(secret);
  return [secret, typedSecretOf(secret)];
}

/** The QR payload, as the pending device shows it and the approver reads it. */
export function approvalPayload({ deviceId, publicKey, secret }) {
  assertSecret(secret);
  return JSON.stringify({ deviceId, publicKey, s: bytesToBase64(secret) });
}

/**
 * @param {string} text what the camera read
 * @returns {{ deviceId: string, publicKey: string, secret: Uint8Array }}
 */
export function parseApprovalPayload(text) {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    throw new ApprovalSecretError(
      APPROVAL_SECRET_ERRORS.MALFORMED_PAYLOAD,
      'That QR code is not a device approval code',
    );
  }
  const malformed = detail =>
    new ApprovalSecretError(APPROVAL_SECRET_ERRORS.MALFORMED_PAYLOAD, detail);
  if (parsed === null || typeof parsed !== 'object') throw malformed('not an object');
  const { deviceId, publicKey, s } = parsed;
  if (typeof deviceId !== 'string' || !deviceId) throw malformed('no device id');
  if (typeof publicKey !== 'string' || !publicKey) throw malformed('no public key');
  let secret;
  try {
    secret = base64ToBytes(s);
  } catch (error) {
    throw malformed('the secret is not base64');
  }
  if (secret.length !== APPROVAL_SECRET_BYTES) throw malformed('the secret is the wrong length');
  return { deviceId, publicKey, secret };
}

/**
 * @param {unknown} secret
 * @returns {asserts secret is Uint8Array}
 */
function assertSecret(secret) {
  if (!(secret instanceof Uint8Array) || secret.length !== APPROVAL_SECRET_BYTES) {
    throw new ApprovalSecretError(
      APPROVAL_SECRET_ERRORS.MALFORMED,
      `An approval secret is ${APPROVAL_SECRET_BYTES} bytes`,
    );
  }
}
